/**
 * Custom Hooks for File Operations
 * Handles saving and loading data from files
 */
import { useState, useCallback, useRef } from 'react';

/**
 * Detects browser support for File System Access API
 */
function useFileSystemSupport() {
  const isFileSystemSupported = 'showSaveFilePicker' in window;
  const isLegacyFileInputSupported =
    typeof window !== 'undefined' &&
    window.File &&
    window.FileReader &&
    window.FileList &&
    window.Blob &&
    window.URL;

  return { isFileSystemSupported, isLegacyFileInputSupported };
}

/**
 * Handles file save operations
 * Supports both modern File System API and download fallback
 */
function useSaveToFile(quadrants, isFileSystemSupported) {
  return useCallback(async () => {
    try {
      if (isFileSystemSupported) {
        const handle = await window.showSaveFilePicker({
          suggestedName: 'time-management-matrix.json',
          types: [{ description: 'JSON File', accept: { 'application/json': ['.json'] } }],
        });
        return { handle, success: true };
      } else {
        // Fallback: download as file
        const blob = new Blob([JSON.stringify(quadrants, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'time-management-matrix.json';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        return { success: true };
      }
    } catch (err) {
      console.error('Save failed:', err);
      return { success: false, error: err };
    }
  }, [quadrants, isFileSystemSupported]);
}

/**
 * Handles file load operations
 * Supports both modern File System API and file input fallback
 */
function useLoadFromFile() {
  const filePickerInput = useRef(null);
  const isPickingFile = useRef(false);

  const loadDataLegacy = useCallback(async () => {
    if (!filePickerInput.current || isPickingFile.current) return;
    isPickingFile.current = true;
    filePickerInput.current.click();
    isPickingFile.current = false;
  }, []);

  const handleFileInputChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      e.target.value = null;
      return { data, success: true };
    } catch (err) {
      console.error('Legacy file load failed:', err);
      return { success: false, error: err };
    }
  }, []);

  return {
    filePickerInput,
    loadDataLegacy,
    handleFileInputChange,
  };
}

/**
 * Manages toast notifications
 */
function useToast() {
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('Saved successfully');

  const showToast = useCallback((message, duration = 3000) => {
    setToastMessage(message);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), duration);
  }, []);

  return { showSaveToast, toastMessage, showToast };
}

export {
  useFileSystemSupport,
  useSaveToFile,
  useLoadFromFile,
  useToast,
};
